import { selectPanoAnnotationsAdjustedPose } from "@/utils/camera-transform";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import { GUID } from "@faro-lotv/foundation";
import {
  IElement,
  isIElementImg360,
  isIElementMarkupImagePoint,
} from "@faro-lotv/ielement-types";
import { equirectangular2DToSphere3D } from "@faro-lotv/lotv";
import {
  IElementsState,
  selectAncestor,
  selectIElement,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { Matrix4, Vector3 } from "three";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "./ielements-slice-selector";
import { curryAppSelector } from "./reselect";

/**
 * @param iElements the iElements state slice
 * @param markup the markup or annotation element or its id used to compute the world position of the annotation
 * @param panoramaImage the panorama element or its id used to compute the world position of the annotation
 * @returns the world position of a 2D annotation in the 3D space of the panorama
 */
export const selectMarkupImagePointWorldPosition = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (iElements: OnlyIElementsStore, markup: GUID | IElement | undefined) =>
        markup,
      (
        iElements: OnlyIElementsStore,
        markup: GUID | IElement | undefined,
        panoramaImage: GUID | IElement | undefined,
      ) => panoramaImage,
    ],
    computeMarkupImagePointWorldPosition,
  ),
);

function computeMarkupImagePointWorldPosition(
  iElements: IElementsState,
  markup: GUID | IElement | undefined,
  panoramaImage: GUID | IElement | undefined,
): Vector3 | undefined {
  const state = { iElements };
  const markupElement =
    typeof markup === "string" ? selectIElement(markup)(state) : markup;

  const annotationElement = selectAncestor(
    markupElement,
    isIElementMarkupImagePoint,
  )(state);

  const panoramaElement =
    typeof panoramaImage === "string"
      ? selectIElement(panoramaImage)(state)
      : panoramaImage;

  if (
    !annotationElement ||
    !panoramaElement ||
    !isIElementImg360(panoramaElement)
  ) {
    return;
  }

  const annotationMatrix = selectIElementWorldMatrix4(annotationElement.id)(
    state,
  );

  const panoAdjustedPose = new Matrix4().fromArray(
    selectPanoAnnotationsAdjustedPose(panoramaElement)(state),
  );

  const pixelPosition = equirectangular2DToSphere3D(
    annotationElement.pixelPosition,
    panoramaElement.pixelWidth,
    panoramaElement.pixelHeight,
  );

  const transform = new Matrix4().multiplyMatrices(
    panoAdjustedPose,
    annotationMatrix,
  );

  return pixelPosition.applyMatrix4(transform);
}
